import React, { useState, useEffect, useRef } from 'react';
import DatePickerModal from './DatePickerModal';
import TimePickerModal from './TimePickerModal';
import Dropdown from './Dropdown';
import ValidationErrorPopup from './CommonPopup';
import {
  RELIGIONS,
  MONTHS,
  DAYS,
  YEARS,
  INITIAL_BIRTH_DETAILS,
  INITIAL_SELECTED_COUNTRY,
  MOBILE_NUMBER_MIN_LENGTH,
  MOBILE_NUMBER_MAX_LENGTH,
  COUNTRIES
} from '../../constants/chatConstants';
import { useAuth } from '../../AuthContext';
import { updateUserProfile, searchPlaces } from '../../api';

export default function BirthDetailsModal({
  show,
  close,
  isFromAuth = false,
  isEdit = false,
  birthDetails,
  setBirthDetails,
  googleUserInfo,
  isLoading,
  handleRegistrationComplete,
  handleProfileUpdate,
  COUNTRIES: propCountries,
  RELIGIONS: tempReligions,
  MONTHS: tempMonths,
  setSelectedCountry,
  handleSearchQueryChange: handleTempSearchQueryChange,
  placeSuggestions: tempPlaceSuggestions,
  isLoadingPlaces: tempIsLoadingPlaces,
  handlePlaceSelect: handleTempPlaceSelect,
  setTempBirthProfile,
  onTempProfileCreated
}) {

  const formatTime = (timeString) => {
    if (!timeString) return "";
    try {
      if (timeString.includes('T')) {
        const timePart = timeString.split('T')[1];
        const [hours, minutes] = timePart.split(':');
        return `${hours.padStart(2, '0')}${minutes.padStart(2, '0')}`;
      }
      if (timeString.includes(':')) {
        const [hours, minutes] = timeString.split(':');
        return `${hours.padStart(2, '0')}${minutes.padStart(2, '0')}`;
      }
      return timeString;
    } catch (error) {
      console.error('Time format error:', error);
      return "";
    }
  };

  const formatTimeDisplay = (timeString) => {
    if (!timeString) return 'Select Time';
    if (timeString.includes(':')) return timeString;
    if (timeString.length === 4) {
      return `${timeString.substring(0, 2)}:${timeString.substring(2, 4)}`;
    }
    return timeString;
  };

  const normalizeReligion = (religion) => {
    if (!religion) return 'Hindu';
    const normalized = religion.charAt(0).toUpperCase() + religion.slice(1).toLowerCase();
    const exists = RELIGIONS.find(r => r.value === normalized);
    return exists ? normalized : 'Hindu';
  };

  const [local, setLocal] = useState({
    ...INITIAL_BIRTH_DETAILS,
    ...birthDetails,
    birth_time: formatTime(birthDetails?.birth_time),
    country_code: birthDetails?.country_code || 'IN',
    religion: normalizeReligion(birthDetails?.religion),
  });

  const [editedDetails, setEditedDetails] = useState({
    ...INITIAL_BIRTH_DETAILS,
    ...birthDetails,
    birth_time: formatTime(birthDetails?.birth_time),
    country_code: birthDetails?.country_code || 'IN',
    religion: normalizeReligion(birthDetails?.religion),
  });

  const [tempSelectedCountry, setTempSelectedCountry] = useState(INITIAL_SELECTED_COUNTRY);
  const [tempSearchQuery, setTempSearchQuery] = useState('');
  const [showPlaceDropdown, setShowPlaceDropdown] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [selectedHour, setSelectedHour] = useState(null);
  const [selectedMinute, setSelectedMinute] = useState(null);
  const [placeSuggestions, setPlaceSuggestions] = useState([]);
  const [isLoadingPlaces, setIsLoadingPlaces] = useState(false);
  const [placeCountries, setPlaceCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const { user, setUser } = useAuth();

  const [validationErrors, setValidationErrors] = useState([]);
  const [showValidationPopup, setShowValidationPopup] = useState(false);
  const [validationIndex, setValidationIndex] = useState(0);

  const placeRef = useRef(null);
  const countryCodeRef = useRef(local.country_code || 'IN');
  const fullNameRef = useRef(null);
  const religionRef = useRef(null);
  const countryCodeNoRef = useRef(null);
  const mobileNumberRef = useRef(null);
  const dateOfBirthRef = useRef(null);
  const birthTimeRef = useRef(null);
  const birthCountryRef = useRef(null);
  const birthPlaceRef = useRef(null);

  // useEffect(() => {
  //   const loadCountries = async () => {
  //     setLoadingCountries(true);
  //     try {
  //       const response = await fetch('/api/countries');
  //       if (response.ok) {
  //         const data = await response.json();
  //         setPlaceCountries(data.countries || []);
  //       }
  //     } catch (error) {
  //       console.error('Failed to load countries:', error);
  //       setPlaceCountries(COUNTRIES);
  //     } finally {
  //       setLoadingCountries(false);
  //     }
  //   };
  //   loadCountries();
  // }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (placeRef.current && !placeRef.current.contains(event.target)) {
        setShowPlaceDropdown(false);
      }
    };

    if (showPlaceDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPlaceDropdown]);

  const validateForm = () => {
    const errors = [];

    if (!local.full_name || !local.full_name.trim()) {
      errors.push({
        field: 'full_name',
        message: 'Full Name is required',
        fieldRef: fullNameRef
      });
    }

    if (!local.religion) {
      errors.push({
        field: 'religion',
        message: 'Religion is required',
        fieldRef: religionRef
      });
    }

    if (!local.birth_day || !local.birth_month || !local.birth_year) {
      errors.push({
        field: 'birth_date',
        message: 'Date of Birth is required',
        fieldRef: dateOfBirthRef
      });
    }

    if (!local.birth_time) {
      errors.push({
        field: 'birth_time',
        message: 'Birth Time is required',
        fieldRef: birthTimeRef
      });
    }

    if (!local.country_code) {
      errors.push({
        field: 'country_code',
        message: 'Country is required',
        fieldRef: birthCountryRef
      });
    }

    if (!local.birth_place || !local.birth_place.trim()) {
      errors.push({
        field: 'birth_place',
        message: 'Birth Place is required',
        fieldRef: birthPlaceRef
      });
    }

    if (isEdit || isFromAuth) {
      if (!local.country_code_no) {
        errors.push({
          field: 'country_code_no',
          message: 'Country code is required',
          fieldRef: countryCodeNoRef
        });
      }

      const mobileNumber = local.mobile_number || '';
      if (!mobileNumber) {
        errors.push({
          field: 'mobile_number',
          message: 'Mobile number is required (7-15 digits)',
          fieldRef: mobileNumberRef
        });
      } else if (!/^\d{7,15}$/.test(mobileNumber)) {
        errors.push({
          field: 'mobile_number',
          message: 'Mobile number must be between 7-15 digits',
          fieldRef: mobileNumberRef
        });
      }
    }

    return errors;
  };

  const handleChange = (field, value) => {
    setLocal(prev => ({ ...prev, [field]: value }));
    setEditedDetails(prev => ({ ...prev, [field]: value }));
    if (setBirthDetails && typeof setBirthDetails === 'function') {
      setBirthDetails(prev => ({ ...prev, [field]: value }));
    }
  };

  const handlePlaceChange = (place) => {
    let formattedPlace;
    if (place.main_text && place.secondary_text) {
      formattedPlace = `${place.main_text.trim()}, ${place.secondary_text.trim()}`;
    } else if (place.description) {
      formattedPlace = place.description;
    } else if (place.formatted_address) {
      formattedPlace = place.formatted_address;
    } else {
      formattedPlace = place.main_text || 'Selected Location';
    }

    handleChange('birth_place', formattedPlace);
    setTempSearchQuery('');
    handleChange('birth_place_search', '');
    setShowPlaceDropdown(false);

    if (isFromAuth && handleTempPlaceSelect) {
      handleTempPlaceSelect(place);
    } else if (handleTempPlaceSelect) {
      handleTempPlaceSelect(place);
    }
  };

  const handleSearchChange = async (value) => {
    setTempSearchQuery(value);
    handleChange('birth_place_search', value);

    if (!value || value.length < 2) {
      setPlaceSuggestions([]);
      return;
    }

    setIsLoadingPlaces(true);
    try {
      const currentCountryCode = countryCodeRef.current || local.country_code || 'IN';
      const response = await searchPlaces(value, currentCountryCode);

      if (response.data?.success) {
        setPlaceSuggestions(response.data.places || []);
      } else {
        setPlaceSuggestions([]);
      }
    } catch (error) {
      console.error(' Place search error:', error);
      setPlaceSuggestions([]);
    } finally {
      setIsLoadingPlaces(false);
    }
  };

  // ✅ NEW: Save temp profile to backend
  const saveTempProfileToBackend = async (tempProfile) => {
    try {
      console.log('📤 Sending temp profile to backend:', tempProfile);

      // ✅ Combine user + temp profile
      const combinedProfile = {
        user: {
          userId: user?.userId || user?.id,
          email: user?.email,
          full_name: user?.full_name,
          religion: user?.religion,
          birth_date: user?.birth_date,
          birth_time: user?.birth_time,
          birth_place: user?.birth_place,
          country_code: user?.country_code
        },
        tempProfile: tempProfile
      };

      const response = await fetch('/astro/temp-profiles', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
        },
        body: JSON.stringify(combinedProfile)
      });

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error(' Non-JSON response:', text);
        throw new Error('Server returned non-JSON response');
      }

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data?.message || 'Failed to save temp profile');
      }

      console.log('✅ Temp profile saved to backend:', data);
      return data;
    } catch (error) {
      console.error(' Failed to save temp profile to backend:', error);
      // Don't throw - allow local storage fallback
      return null;
    }
  };

  const handleSubmit = async () => {
    const errors = validateForm();
    if (errors.length > 0) {
      setValidationErrors(errors);
      setValidationIndex(0);
      setShowValidationPopup(true);
      return;
    }

    // ✅ EDIT MODE - Update profile via API
    if (isEdit && user) {
      setIsUpdating(true);
      try {
        const birth_date = `${editedDetails.birth_year}-${String(editedDetails.birth_month).padStart(2, '0')}-${String(editedDetails.birth_day).padStart(2, '0')}`;

        const response = await updateUserProfile(user.userId || user.id, {
          full_name: editedDetails.full_name,
          religion: editedDetails.religion,
          birth_date: birth_date,
          birth_time: editedDetails.birth_time,
          birth_place: editedDetails.birth_place,
          country_code_no: editedDetails.country_code_no,
          mobile_number: editedDetails.mobile_number,
          country_code: editedDetails.country_code || 'IN'
        });

        if (response?.data?.success) {
          if (setUser && response.data.user) {
            setUser(response.data.user);
          }
          alert('✅ Profile updated successfully!');
          close();
        } else {
          throw new Error(response?.data?.message || 'Update failed');
        }
      } catch (error) {
        console.error(' Update error:', error);
        alert(` Failed to update profile: ${error.message}`);
      } finally {
        setIsUpdating(false);
      }
    }
    // ✅ AUTH MODE - Complete registration
    else if (isFromAuth && handleRegistrationComplete) {
      handleRegistrationComplete();
    }
    // ✅ TEMP PROFILE MODE - Save to backend AND sessionStorage
    else {
      setIsUpdating(true);
      try {
        const birth_date = `${local.birth_year}-${String(local.birth_month).padStart(2, '0')}-${String(local.birth_day).padStart(2, '0')}`;
        
        const tempProfile = {
          full_name: local.full_name,
          religion: local.religion,
          birth_date: birth_date,
          birth_time: local.birth_time,
          birth_place: local.birth_place,
          country_code: local.country_code || 'IN',
          given_name: (local.full_name || '').split(' ')[0] || 'User',
          timestamp: new Date().toISOString(),
          isTempProfile: true
        };

        // ✅ Save to backend
        await saveTempProfileToBackend(tempProfile);

        // ✅ Save to sessionStorage (fallback)
        sessionStorage.setItem('astroguru_temp_profile', JSON.stringify(tempProfile));

        // ✅ Update state
        if (setTempBirthProfile) {
          setTempBirthProfile(tempProfile);
        }

        // ✅ Callback
        if (onTempProfileCreated) {
          onTempProfileCreated(tempProfile);
        }

        console.log('✅ Temp profile created:', tempProfile);
        alert('✅ Temp profile created successfully!');
        close();
      } catch (error) {
        console.error(' Temp profile creation error:', error);
        alert(` Failed to create temp profile: ${error.message}`);
      } finally {
        setIsUpdating(false);
      }
    }
  };

  // ✅ Handle popup close - Move to next error
  const handlePopupClose = () => {
    setShowValidationPopup(false);
    setValidationIndex(prev => prev + 1);
  };

  if (!show) return null;

  const actualPlaceSuggestions = placeSuggestions;
  const actualIsLoadingPlaces = isLoadingPlaces;
  const religionsToUse = isFromAuth ? RELIGIONS : (tempReligions || RELIGIONS);
  const monthsToUse = isFromAuth ? MONTHS : (tempMonths || MONTHS);
  const COUNTRIES_TO_USE = propCountries || COUNTRIES;

  const countriesData = isFromAuth
    ? ((placeCountries && placeCountries.length > 0) ? placeCountries : COUNTRIES_TO_USE)
    : COUNTRIES_TO_USE;

  const selectedCountryData = isFromAuth
    ? (placeCountries?.find(c => c.iso === local.country_code) || INITIAL_SELECTED_COUNTRY)
    : (tempSelectedCountry?.iso === local.country_code ? tempSelectedCountry : COUNTRIES?.find(c => c.iso === local.country_code) || tempSelectedCountry);

  // ✅ Get current error to display (only one at a time)
  const currentError = validationErrors.length > 0 && validationIndex < validationErrors.length
    ? [validationErrors[validationIndex]]
    : [];

  return (
    <>
      <ValidationErrorPopup
        isOpen={showValidationPopup}
        onClose={handlePopupClose}
        errors={currentError}
      />

      <div className="modal-overlay">
        <div className="birth-details-modal">
          <button className="modal-close-btn" onClick={close}>×</button>

          <div className="modal-header">
            <h2 className="modal-title">
              {isEdit
                ? '✏️ Edit Your Profile'
                : isFromAuth
                  ? `Welcome, ${googleUserInfo?.name}!`
                  : 'Ask for Someone Else'
              }
            </h2>
            <p className="modal-subtitle">
              {isEdit
                ? 'Update your cosmic profile information'
                : isFromAuth
                  ? 'Complete Your Cosmic Profile'
                  : 'Create a temporary cosmic profile for someone else.'
              }
            </p>
          </div>

          <div className="birth-form">
            <div className="form-grid">
              <div className="form-row">
                <div className="form-field" style={{ flex: "1.5" }}>
                  <label className="form-label">Full Name</label>
                  <input
                    ref={fullNameRef}
                    type="text"
                    value={local.full_name || ''}
                    onChange={(e) => handleChange('full_name', e.target.value)}
                    placeholder="Enter complete name"
                    className="form-input"
                    style={{ height: "52px" }}
                  />
                </div>

                <div className="form-field" style={{ flex: "1" }}>
                  <label className="form-label">Religion</label>
                  <div ref={religionRef}>
                    <Dropdown
                      items={religionsToUse.map((r) => ({
                        label: `${r.label}`,
                        value: r.value
                      }))}
                      value={local.religion}
                      onChange={(val) => handleChange('religion', val)}
                      placeholder="Select Religion"
                    />
                  </div>
                </div>
              </div>

              {(isEdit || isFromAuth) && (
                <div className="form-row">
                  <div className="form-field" style={{ flex: '0.3' }}>
                    <label className="form-label">Country</label>
                    <div ref={countryCodeNoRef}>
                      <Dropdown
                        items={
                          (isFromAuth
                            ? ((placeCountries && placeCountries.length > 0) ? placeCountries : COUNTRIES_TO_USE)
                            : COUNTRIES_TO_USE
                          ).map(c => ({
                            label: `${c.isdCode} ${c.name}`,
                            value: c.isdCode,
                            displayCode: c.isdCode
                          }))
                        }
                        value={local.country_code_no || '+91'}
                        onChange={(val) => handleChange('country_code_no', val)}
                        displayValue="displayCode"
                        placeholder={loadingCountries ? "Loading..." : "+91"}
                        disabled={loadingCountries}
                      />
                    </div>
                  </div>

                  <div className="form-field" style={{ flex: '1.3' }}>
                    <label className="form-label">Mobile Number</label>
                    <input
                      ref={mobileNumberRef}
                      type="tel"
                      value={local.mobile_number || ''}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        if (value.length <= MOBILE_NUMBER_MAX_LENGTH) {
                          handleChange('mobile_number', value);
                        }
                      }}
                      placeholder="Enter mobile number"
                      className="form-input"
                      maxLength={MOBILE_NUMBER_MAX_LENGTH}
                      minLength={MOBILE_NUMBER_MIN_LENGTH}
                      required
                    />
                  </div>
                </div>
              )}

              <div className="form-grid-optimized">
                <div className="form-row">
                  <div className="form-field">
                    <label className="form-label">Date of Birth</label>
                    <button
                      ref={dateOfBirthRef}
                      type="button"
                      className="form-dropdown-container"
                      onClick={() => setShowDatePicker(true)}
                      style={{ color: 'white', cursor: 'pointer' }}
                    >
                      <span className="gap-button">📅</span>
                      <span className="date-text">
                        {local.birth_day && local.birth_month && local.birth_year
                          ? `${String(local.birth_day).padStart(2, '0')} ${monthsToUse.find(m => m.value == local.birth_month)?.name.substring(0, 3)} ${local.birth_year}`
                          : 'Select Date'
                        }
                      </span>
                    </button>
                  </div>

                  <div className="form-field">
                    <label className="form-label">Birth Time</label>
                    <button
                      ref={birthTimeRef}
                      type="button"
                      className="form-dropdown-container"
                      onClick={() => setShowTimePicker(true)}
                      style={{ color: 'white', cursor: 'pointer' }}
                    >
                      <span className="gap-button">🕐</span>
                      <span className="time-text">
                        {formatTimeDisplay(local.birth_time)}
                      </span>
                    </button>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-field" style={{ flex: '1' }}>
                    <label className="form-label">Country</label>
                    <div ref={birthCountryRef}>
                      <Dropdown
                        items={
                          (isFromAuth
                            ? ((placeCountries && placeCountries.length > 0) ? placeCountries : COUNTRIES_TO_USE)
                            : COUNTRIES_TO_USE
                          ).map(c => ({
                            label: `${c.flag || '🌍'} ${c.name}`,
                            value: c.iso || c.code || c.countryCode,
                            displayCode: c.flag || '🌍'
                          }))
                        }
                        value={local.country_code || 'IN'}
                        onChange={(val, item) => {
                          handleChange('country_code', val);
                          countryCodeRef.current = val;
                        }}
                        displayValue="displayCode"
                        placeholder={loadingCountries ? "Loading..." : "🇮🇳"}
                        disabled={loadingCountries}
                      />
                    </div>
                  </div>

                  <div className="form-field" ref={birthPlaceRef}>
                    <label className="form-label">Birth Place</label>
                    <div className="form-dropdown-container">
                      {!showPlaceDropdown && (
                        <div
                          className="form-dropdown-trigger"
                          onClick={() => setShowPlaceDropdown(!showPlaceDropdown)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="form-dropdown-content">
                            <span className="form-icon">🏙️</span>
                            <div className="form-name">
                              {local.birth_place || `Select city in ${selectedCountryData?.name || 'India'}`}
                            </div>
                          </div>
                        </div>
                      )}

                      {showPlaceDropdown && (
                        <>
                          <input
                            type="text"
                            value={tempSearchQuery}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            placeholder={`Type city in ${selectedCountryData?.name || 'India'}...`}
                            className="form-dropdown-content"
                            autoComplete="off"
                            autoFocus
                            style={{ padding: "3px 0px" }}
                          />
                          {actualIsLoadingPlaces && (
                            <div className="place-loading-icon">
                              <span style={{ animation: 'spin 1s linear infinite' }}>🔄</span>
                            </div>
                          )}

                          <div className="form-dropdown-menu" style={{ width: "auto" }}>
                            {actualPlaceSuggestions?.length > 0 ? (
                              actualPlaceSuggestions.map((place, index) => (
                                <div
                                  key={place.place_id || index}
                                  className="form-dropdown-item"
                                  onClick={() => handlePlaceChange(place)}
                                >
                                  <div className="form-dropdown-content">
                                    <div className="form-icon">📍</div>
                                    <div className="form-name">
                                      <div className="place-item-main">
                                        {place.main_text}
                                        {place.importance > 0.5 && <span className="place-item-popular">⭐</span>}
                                      </div>
                                      <div className="place-item-secondary">
                                        {place.secondary_text}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))
                            ) : tempSearchQuery.length > 2 && !actualIsLoadingPlaces ? (
                              <div className="place-dropdown-no-results">
                                <div className="no-results-icon">🔍</div>
                                <div className="no-results-text">
                                  No cities found for "{tempSearchQuery}"
                                </div>
                              </div>
                            ) : (
                              <div className="place-dropdown-hint">
                                <div className="hint-icon">💡</div>
                                <div className="hint-text">Start typing to search cities...</div>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-buttons">
              <button
                className="btn-primary"
                onClick={handleSubmit}
                disabled={isUpdating || ((isFromAuth || isEdit) && isLoading)}
              >
                {isUpdating
                  ? '⏳ Updating...'
                  : isEdit
                    ? '💾 Save Changes'
                    : isFromAuth
                      ? '🚀 Complete Registration'
                      : '🎭 Create Temp Profile'
                }
              </button>
            </div>
          </div>
        </div>

        {showDatePicker && (
          <DatePickerModal
            isOpen={showDatePicker}
            onClose={() => setShowDatePicker(false)}
            birthDetails={local}
            handleBirthDetailsChange={handleChange}
            getDaysInMonth={() => {
              if (!local.birth_month || !local.birth_year) return 31;
              const month = parseInt(local.birth_month);
              const year = parseInt(local.birth_year);
              return new Date(year, month, 0).getDate();
            }}
          />
        )}

        {showTimePicker && (
          <TimePickerModal
            isOpen={showTimePicker}
            onClose={() => setShowTimePicker(false)}
            birthDetails={local}
            handleBirthDetailsChange={handleChange}
            selectedHour={selectedHour}
            setSelectedHour={setSelectedHour}
            selectedMinute={selectedMinute}
            setSelectedMinute={setSelectedMinute}
          />
        )}
      </div>
    </>
  );
}
